/* Bundle sheet (Luka 2026-10-05, "A2"): the phone builder's bundle is ONE physical pane that slides up from behind a
   pinned checkout row (the pane's own foot: total, YOU SAVE, the button), 1:1 with the finger. Closed = the grabber and
   the pane's top edge peeking above the row. Tap, drag, fling, ✕, Escape and the scrim all move the same value through
   the same settle, and everything else (the backdrop, the row's place, the button's words, the head's actions) is derived
   from that position, so nothing pops.

   One state: y, the pane's translateY in px. 0 = open (the pane's bottom on the screen's bottom; its bottom padding is the
   row's height, so its last item clears the row); PEEK = closed (pane height - row height - peek band); HIDDEN = off-screen
   (an empty bundle: the row goes with it). The row is the pane's foot, counter-moved by -min(y, PEEK): pinned to the screen
   while the pane slides, and carried away with it below PEEK.

   Physics from the proven sheets (research: ~/.bb-canary/dock/sheet-research.md):
   - the iOS curve cubic-bezier(0.32, 0.72, 0, 1), 500 ms for a full trip (Vaul, Ionic); a release keeps the finger's speed;
   - a 10 px start threshold with an axis lock (Vaul, Ionic, cupertino-pane);
   - velocity as Ionic's smoothed average (0.7 new + 0.3 previous), frames over 100 ms apart ignored;
   - the release: under 20 px back; a flick over 0.4 px/ms one stop in its direction (Vaul); else the position projected
     250 ms ahead picks the nearest stop (react-modal-sheet 0.2 s / Ionic v7 0.35 s);
   - the rubber band past the ends d·D·0.55 / (D + 0.55·d) (use-gesture / react-spring-bottom-sheet);
   - a grab mid-settle continues from the on-screen position (cupertino-pane);
   - no click after a drag (cupertino-pane's preventClicks).
   iOS Safari: touch events (non-passive touchmove) for touch, so a drag that starts on the list at its top can take the
   gesture before the browser scrolls; pointer events only for a mouse. Reduced motion: drags still follow the finger (the
   user moves it); settles land at once. */
(() => {
  const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
  const FULL_MS = 500;
  const MIN_MS = 220;
  const START_PX = 10;
  const BACK_PX = 20;
  const FLICK = 0.4; // px/ms
  const PROJECT_MS = 250;
  const DISMISS = 1 / 3; // a sheet without a peek closes once dragged (or projected) a third of the way
  const RUBBER = 0.55;
  const RUBBER_TOP = 140; // the band's bound past open
  const RUBBER_BOTTOM = 40; // and below the peek (the row stays)
  const rubber = (d, D) => (d * D * RUBBER) / (D + RUBBER * d);
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const matrixY = (el) => {
    const t = getComputedStyle(el).transform;
    if (!t || t === 'none') return 0;
    const m = t.match(/matrix(3d)?\(([^)]+)\)/);
    if (!m) return 0;
    const v = m[2].split(',').map(Number);
    return m[1] ? v[13] : v[5];
  };

  /**
   * o: { pane, foot, scrim, peek(): px, docked(): bool (the bundle shows), reduce(): bool,
   *      scroller(): the element that scrolls inside the pane (the pane),
   *      dragFrom(target): bool (a surface that always drags: the grabber, the head, the row),
   *      onDragStart(), onRelease(open: bool) (the builder settles through setSheet), onFrame(p), onMeasure({ dockH }) }
   */
  function create(o) {
    const { pane, foot, scrim } = o;
    let on = false; // phones only; desktop keeps the pane as a column
    let y = 0; // the last position written
    let stops = { open: 0, peek: 0, hidden: 0 };
    let at = 'hidden'; // the stop the sheet rests at or is settling to
    let settling = false;
    let resting = false; // exactly at a stop: a content change may move it to the stop's new place
    let raf = 0;
    let frameRaf = 0;

    const closedY = () => (o.docked() ? stops.peek : stops.hidden);
    const pOf = (v) => { const c = closedY(); return c > 0 ? clamp(1 - v / c, 0, 1) : 0; };
    function write(v, ms) {
      y = v;
      const tr = ms ? `transform ${ms}ms ${EASE}` : 'none';
      pane.style.transition = tr;
      pane.style.transform = `translate3d(0, ${v}px, 0)`;
      if (foot) {
        foot.style.transition = tr;
        // pinned between open and the peek; past open (the rubber band) it rides with the pane, so the pane's edge never
        // cuts it (Luka: "dragging up beyond the height of the pane crops the button container"); below the peek it rides
        // with the pane too, ALWAYS at its peek place on it, docked or not: the bar's entrance and exit (the Stage
        // scrolling away / back, an emptied bundle) are then one block sliding, row and peek together. Luka's iPhone
        // (rec-07-59-34, "way too harsh"): unpinned while away, the row travelled three times the pane's distance on
        // the same curve, so the pane's own head (Your bundle · Undo · Clear) showed above it mid-way
        foot.style.transform = `translate3d(0, ${-clamp(v, 0, stops.peek)}px, 0)`;
      }
      if (scrim) {
        const p = pOf(v);
        scrim.style.transition = ms ? `opacity ${ms}ms ${EASE}` : 'none';
        scrim.style.opacity = String(p);
        // it takes taps only for an open sheet (at rest or settling there): a closed sheet's scrim is never a wall, even at
        // a faint opacity (it caught taps at 0.01-0.03 while the page settled, 2026-10-07)
        scrim.style.pointerEvents = at === 'open' ? 'auto' : 'none';
      }
    }
    /** Derived per frame while moving (the compositor runs the transform; this reads where it is). */
    function frames(until) {
      cancelAnimationFrame(frameRaf);
      const tick = () => {
        const v = matrixY(pane);
        if (o.onFrame) o.onFrame(pOf(v), v);
        if (performance.now() < until) frameRaf = requestAnimationFrame(tick);
        else if (o.onFrame) o.onFrame(pOf(y), y);
      };
      frameRaf = requestAnimationFrame(tick);
    }
    function measure() {
      if (!on) return;
      const h = pane.offsetHeight;
      const fh = foot ? foot.offsetHeight : 0;
      const peek = Math.round(o.peek());
      stops = { open: 0, peek: Math.max(0, h - fh - peek), hidden: h + 60 };
      if (o.onMeasure) o.onMeasure({ dockH: fh + peek, footH: fh });
      if (resting) write(stops[at], 0); // a content change at rest keeps the stop, with no motion (never after a drag)
      else if (settling && !drag && Math.abs(stops[at] - y) > 0.5) settle(at); // …and mid-flight the move re-aims at the stop's new place, from where it is (it
      // landed a few px off the closed stop as the page settled, and the scrim read 1-3 % open there)
    }
    /** The one animator: from wherever the pane is to a stop, keeping a release's speed. */
    function settle(stop, v = 0) {
      if (!on) return;
      at = stop;
      resting = false;
      const from = settling ? matrixY(pane) : y;
      if (settling) write(from, 0); // a new settle mid-flight starts from the on-screen place
      const to = stops[stop];
      const d = Math.abs(to - from);
      let ms = 0;
      if (!o.reduce() && d > 0.5) {
        ms = Math.abs(v) > 0.05 ? clamp((2.25 * d) / Math.abs(v), MIN_MS, FULL_MS)
          : clamp(FULL_MS * Math.sqrt(d / Math.max(1, stops.peek || d)), 280, FULL_MS);
      }
      // the new transform is set in the next frame so the transition starts from the written place
      cancelAnimationFrame(raf);
      settling = ms > 0;
      raf = requestAnimationFrame(() => {
        write(to, Math.round(ms));
        if (ms) frames(performance.now() + ms + 32);
        else { resting = true; if (o.onFrame) o.onFrame(pOf(to), to); if (o.onSettled) o.onSettled(at); }
      });
      if (!ms) settling = false;
    }
    pane.addEventListener('transitionend', (e) => { if (e.target === pane && e.propertyName === 'transform' && !drag) { settling = false; resting = true; if (o.onSettled) o.onSettled(at); } });

    // ---- the gesture -------------------------------------------------------------------------------------------------
    let drag = null; // { x0, y0, from, live, v, t, last, scroll }
    let swallow = 0;
    function begin(x, yy, t, target) {
      if (!on) return;
      if (!o.docked() && at !== 'open') return; // a sheet without a peek is only dragged while it shows
      const fromScreen = settling ? matrixY(pane) : y;
      const surface = o.dragFrom(target);
      // ONE owner per touch, decided as it starts (Luka's iPhone, round 2 note 1: the pane dragged AND its content scrolled,
      // opening a gap): a list already scrolled away from its top keeps the whole touch; the sheet never takes it midway
      const sc = !surface && at === 'open' ? o.scroller() : null;
      const listOwns = !!(sc && sc.scrollTop > 0);
      drag = { x0: x, y0: yy, from: fromScreen, live: false, abandoned: listOwns, v: 0, t, last: yy, surface, open: at === 'open' };
      resting = false;
      if (settling) { cancelAnimationFrame(raf); settling = false; write(fromScreen, 0); } // caught mid-flight
      pane.classList.add('is-pressed');
    }
    function move(x, yy, t, e) {
      if (!drag || drag.abandoned) return;
      const dx = x - drag.x0;
      const dy = yy - drag.y0;
      if (!drag.live) {
        // iOS commits a touch to native scrolling at its first move that isn't cancelled, so the sheet decides at once:
        // sideways (the picture strip) or up on the list (it scrolls) lets go; down, or still too small to tell, is held
        // (cancelled) so the list never starts a scroll or a rubber band under a drag, and the drag shows from 10 px
        if (Math.abs(dx) > Math.abs(dy) + 1) { drag.abandoned = true; return; } // sideways: not ours
        if (drag.open && !drag.surface && dy < -1) { drag.abandoned = true; return; } // up on the list: it scrolls
        if (e && e.cancelable) e.preventDefault();
        if (Math.abs(dy) < START_PX && Math.abs(dx) < START_PX) return;
        drag.live = true;
        drag.y0 = yy; // the threshold isn't a jump
        drag.last = yy;
        drag.t = t;
        if (o.onDragStart) o.onDragStart();
      }
      if (e && e.cancelable) e.preventDefault();
      const dt = t - drag.t;
      if (dt > 0 && dt < 100) drag.v = 0.7 * ((yy - drag.last) / dt) + 0.3 * drag.v;
      else if (dt >= 100) drag.v = 0;
      drag.t = t;
      drag.last = yy;
      let v = drag.from + (yy - drag.y0);
      if (v < 0) v = -rubber(-v, RUBBER_TOP);
      else if (o.docked() && v > stops.peek) v = stops.peek + rubber(v - stops.peek, RUBBER_BOTTOM);
      write(v, 0);
      if (o.onFrame) o.onFrame(pOf(v), v);
    }
    function end() {
      pane.classList.remove('is-pressed');
      const d = drag;
      drag = null;
      if (!d || !d.live) return;
      swallow = performance.now() + 400; // the click a drag ends with is not a tap
      const moved = Math.abs(y - d.from);
      let open;
      if (moved < BACK_PX) open = d.open;
      else if (Math.abs(d.v) > FLICK) open = d.v < 0;
      else {
        const proj = y + d.v * PROJECT_MS;
        // two stops (the peek): the nearest; a sheet that dismisses goes once it is a third of the way (Vaul: a quarter)
        open = o.docked() ? Math.abs(proj - stops.open) < Math.abs(proj - closedY()) : proj < closedY() * DISMISS;
      }
      o.onRelease(open, d.v);
    }
    // touch (iOS): non-passive moves so a drag can take the gesture before the browser scrolls
    pane.addEventListener('touchstart', (e) => { if (e.touches.length === 1) { const p = e.touches[0]; begin(p.clientX, p.clientY, e.timeStamp, e.target); } else { drag = null; } }, { passive: true });
    pane.addEventListener('touchmove', (e) => { const p = e.touches[0]; if (p) move(p.clientX, p.clientY, e.timeStamp, e); }, { passive: false });
    pane.addEventListener('touchend', end);
    pane.addEventListener('touchcancel', end);
    // a mouse (a narrow desktop window): the same gesture through pointer events
    pane.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      begin(e.clientX, e.clientY, e.timeStamp, e.target);
      const mv = (ev) => move(ev.clientX, ev.clientY, ev.timeStamp, ev);
      const up = () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up); end(); };
      window.addEventListener('pointermove', mv);
      window.addEventListener('pointerup', up);
    });
    window.addEventListener('click', (e) => {
      if (performance.now() < swallow && pane.contains(e.target)) { e.preventDefault(); e.stopPropagation(); swallow = 0; }
    }, true);

    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(() => measure());
      ro.observe(pane);
      if (foot) ro.observe(foot);
    }
    window.addEventListener('resize', () => measure());

    return {
      enable() {
        if (on) return;
        on = true;
        at = o.docked() ? 'peek' : 'hidden';
        resting = true;
        measure();
        write(stops[at], 0);
        if (o.onFrame) o.onFrame(pOf(y), y);
      },
      disable() {
        on = false;
        drag = null;
        settling = false;
        cancelAnimationFrame(raf);
        for (const el of [pane, foot, scrim]) if (el) { el.style.transition = ''; el.style.transform = ''; }
        if (scrim) { scrim.style.opacity = ''; scrim.style.pointerEvents = ''; }
      },
      /** open (true), closed (false: the peek, or off-screen when the bundle is empty) */
      to(open, v) { settle(open ? 'open' : (o.docked() ? 'peek' : 'hidden'), v); },
      rest() { if (at !== 'open') settle(o.docked() ? 'peek' : 'hidden'); },
      measure,
      get on() { return on; },
      get at() { return at; },
      get dragging() { return !!(drag && drag.live); },
    };
  }
  window.GSIBundleSheet = { create };
})();
